import React, { useState, useEffect } from 'react';
import { View, Text, Flex, Heading, Table, Badge, Link } from '@smarterservices/smarter-ui';
import DraggableModal from '@ui/shared/components/Common/DraggableModal/DraggableModal';
import { usePlayerStore } from '../../../stores/StoreContext';

function TrackDebugModal({ isOpen, onClose }) {
  const tracks = usePlayerStore((state) => state.tracks);
  const currentTime = usePlayerStore((state) => state.currentTime);
  const getTrackStatusAtTime = usePlayerStore((state) => state.getTrackStatusAtTime);
  const isLive = usePlayerStore((state) => state.isLive);
  const jumpTo = usePlayerStore((state) => state.jumpTo);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const handleTimeClick = (sessionChangeTime) => {
    const targetTime = Math.max(0, sessionChangeTime - 10);
    jumpTo(targetTime);
  };

  useEffect(() => {
    if (isOpen) {
      setLastUpdate(new Date());
      console.log('[TrackDebugModal] Tracks:', tracks);
    }
  }, [tracks, isOpen]);

  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return 'N/A';
    // Ensure negative times are set to 0
    const safeSeconds = Math.max(0, seconds);
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const secs = Math.floor(safeSeconds % 60);
    return hours + ':' + minutes.toString().padStart(2, '0') + ':' + secs.toString().padStart(2, '0');
  };

  const trackCount = tracks && typeof tracks === 'object' ? Object.keys(tracks).length : 0;

  return (
    <DraggableModal
      isOpen={isOpen}
      onClose={onClose}
      initialPosition="top right"
      title="Track Debug Information"
      initialSize={{ width: 900, height: 800 }}
      minSize={{ width: 600, height: 400 }}
      resizable={true}
      minimizable={true}
      constrainToContainer={true}
      maintainPositionOnClose={true}
    >
      <View as="div" padding="small">
        <Flex direction="column" gap="medium">
          <View as="div">
            <Heading level="h3" margin="none none small">Summary</Heading>
            <Table caption="Track Summary">
              <Table.Head>
                <Table.Row>
                  <Table.ColHeader id="metric">Metric</Table.ColHeader>
                  <Table.ColHeader id="value">Value</Table.ColHeader>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                <Table.Row>
                  <Table.Cell>Total Tracks</Table.Cell>
                  <Table.Cell>{trackCount}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>Current Time</Table.Cell>
                  <Table.Cell>{formatTime(currentTime)}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>Mode</Table.Cell>
                  <Table.Cell>{isLive ? 'Live' : 'Recorded'}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>Last Update</Table.Cell>
                  <Table.Cell>{lastUpdate.toLocaleTimeString()}</Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
          </View>

          <View as="div">
            <Heading level="h3" margin="none none small">All Tracks ({trackCount})</Heading>
            {trackCount === 0 ? (
              <Text>No tracks loaded</Text>
            ) : (
              <Table caption="Track Details">
                <Table.Head>
                  <Table.Row>
                    <Table.ColHeader id="hlsTrackId">Track ID</Table.ColHeader>
                    <Table.ColHeader id="status">Status</Table.ColHeader>
                    <Table.ColHeader id="type">Type</Table.ColHeader>
                    <Table.ColHeader id="source">Source</Table.ColHeader>
                    <Table.ColHeader id="identity">Identity</Table.ColHeader>
                    <Table.ColHeader id="name">Name</Table.ColHeader>
                    <Table.ColHeader id="kind">Kind</Table.ColHeader>
                    <Table.ColHeader id="hls">HLS</Table.ColHeader>
                    <Table.ColHeader id="webrtc">WebRTC</Table.ColHeader>
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  {tracks && Object.keys(tracks)
                    .filter((trackId) => tracks[trackId])
                    .map((trackId) => {
                      try {
                        const track = tracks[trackId];
                        if (!track) return null;

                        const currentStatus = getTrackStatusAtTime ? getTrackStatusAtTime(track, currentTime) : null;
                        const hasHLS = track.hasRecordedData && track.hasRecordedData();
                        const hasWebRTC = track.hasLiveData && track.hasLiveData();
                        const hlsTrackId = track.hls && track.hls.track && track.hls.track.id ? track.hls.track.id : 'N/A';

                        return (
                          <Table.Row key={trackId}>
                            <Table.Cell>{track.hlsTrackId}</Table.Cell>
                            <Table.Cell>{currentStatus || 'N/A'}</Table.Cell>
                            <Table.Cell>{track.participantType || 'Unknown'}</Table.Cell>
                            <Table.Cell>{track.source || 'Unknown'}</Table.Cell>
                            <Table.Cell>{track.participantIdentity || 'N/A'}</Table.Cell>
                            <Table.Cell>{track.name || 'N/A'}</Table.Cell>
                            <Table.Cell>{track.kind || 'N/A'}</Table.Cell>
                            <Table.Cell>{hasHLS ? 'Yes' : 'No'}</Table.Cell>
                            <Table.Cell>{hasWebRTC ? 'Yes' : 'No'}</Table.Cell>
                          </Table.Row>
                        );
                      } catch (error) {
                        console.error('[TrackDebugModal] Error rendering track row:', trackId, error);
                        return null;
                      }
                    })
                    .filter(Boolean)}
                </Table.Body>
              </Table>
            )}
          </View>

          <View as="div">
            <Heading level="h3" margin="none none small">Track Status History</Heading>
            {trackCount === 0 ? (
              <Text>No tracks loaded</Text>
            ) : (
              <Flex direction="column" gap="small">
                {tracks && Object.keys(tracks)
                  .filter((trackId) => {
                    const track = tracks[trackId];
                    return track && track.hasRecordedData && track.hasRecordedData() &&
                      track.hls && track.hls.track && track.hls.track.statuses;
                  })
                  .map((trackId) => {
                    try {
                      const track = tracks[trackId];
                      if (!track) return null;

                      return (
                        <View key={trackId} as="div" padding="small" background="secondary" borderRadius="medium">
                          <Text weight="bold" margin="none none small">{track.participantType} - {track.source}</Text>
                          <Table caption={trackId}>
                            <Table.Head>
                              <Table.Row>
                                <Table.ColHeader id="time">Time</Table.ColHeader>
                                <Table.ColHeader id="status">Status</Table.ColHeader>
                                <Table.ColHeader id="trackId">Track ID</Table.ColHeader>
                                <Table.ColHeader id="date">Date</Table.ColHeader>
                              </Table.Row>
                            </Table.Head>
                            <Table.Body>
                              {track.hls.track.statuses.map((status, idx) => (
                                <Table.Row key={idx}>
                                  <Table.Cell>
                                    <Link
                                      onClick={() => handleTimeClick(status.sessionChangeTime)}
                                    >
                                      {formatTime(status.sessionChangeTime)}
                                    </Link>
                                  </Table.Cell>
                                  <Table.Cell>{status.status}</Table.Cell>
                                  <Table.Cell>{status.trackId}</Table.Cell>
                                  <Table.Cell>{new Date(status.changeDate).toLocaleString()}</Table.Cell>
                                </Table.Row>
                              ))}
                            </Table.Body>
                          </Table>
                        </View>
                      );
                    } catch (error) {
                      console.error('[TrackDebugModal] Error rendering status history:', trackId, error);
                      return null;
                    }
                  })
                  .filter(Boolean)}
              </Flex>
            )}
          </View>
        </Flex>
      </View>
    </DraggableModal>
  );
}

export default TrackDebugModal;
